import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { getMediaUrl, getImageUrl, optimizeCloudinaryImage } from '../../../lib/mediaUrl';
import { CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Star, MapPin, Phone, Gift, X, Clock, Scissors, Palette, Droplets, Waves, Sparkles } from 'lucide-react';
import { getCategoryInfo } from '../../../lib/categories';
import { SOCIAL_LINKS } from '../../../lib/websiteConstants';
import { loadPublicTrends } from '../trendsSource';

const _SITE_EASE = [0.22, 1, 0.36, 1];

// ── Linguaggio visivo condiviso del sito pubblico ("via di mezzo": salone elegante) ──
export const DISPLAY_FONT = "'Playfair Display', Georgia, serif";
export const BRAND = {
  cream: '#FDF8F5',
  espresso: '#1A0A10',
  espressoSoft: '#24121A',
  blush: '#F8EEEC', // crema rosata: alterna le sezioni chiare
  rose: '#A94C63', // rosa scurito: testo bianco ≥ 4.5:1
  gold: '#D4AF7A',
  text: '#2D1B14',
  muted: '#6B4A3C',
};
// Un solo stile per l'azione primaria (prenotare) in tutta la pagina.
// *_CORE = solo colori/forma (dimensioni a parte, per evitare classi Tailwind in conflitto).
const CTA_SHAPE = 'inline-flex items-center justify-center gap-2 rounded-full font-bold tracking-wider uppercase transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2';
const CTA_SIZE = 'min-h-[48px] px-8 text-sm';
export const CTA_PRIMARY_CORE = `${CTA_SHAPE} bg-[#A94C63] hover:bg-[#8F3F54] text-white focus-visible:ring-[#D4AF7A] focus-visible:ring-offset-2`;
export const CTA_PRIMARY = `${CTA_PRIMARY_CORE} ${CTA_SIZE}`;
// Azioni secondarie: contorno (su sfondo scuro / su sfondo chiaro)
export const CTA_OUTLINE_DARK_CORE = `${CTA_SHAPE} border border-[#D4AF7A]/70 text-white hover:bg-white/10 focus-visible:ring-[#D4AF7A]`;
export const CTA_OUTLINE_DARK = `${CTA_OUTLINE_DARK_CORE} ${CTA_SIZE}`;
export const CTA_OUTLINE_LIGHT_CORE = `${CTA_SHAPE} border border-[#1A0A10] text-[#1A0A10] hover:bg-[#1A0A10] hover:text-white focus-visible:ring-[#A94C63]`;
export const CTA_OUTLINE_LIGHT = `${CTA_OUTLINE_LIGHT_CORE} ${CTA_SIZE}`;

function AnimatedSection({ children, className = '', delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, ease: _SITE_EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export { AnimatedSection };

// Filo d'oro sotto i titoli: si "disegna" dal centro quando il titolo entra in vista
export function GoldRule({ className = 'mx-auto mt-6' }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden="true"
      className={`w-14 h-px bg-[#D4AF7A] ${className}`}
      initial={reduce ? false : { scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, ease: _SITE_EASE, delay: 0.15 }}
    />
  );
}

// Griglie: le card salgono una dopo l'altra (70 ms di scarto)
const STAGGER = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const RISE = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: _SITE_EASE } } };
function useStaggerProps() {
  const reduce = useReducedMotion();
  return { variants: STAGGER, initial: reduce ? false : 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-40px' } };
}

// ── Categorie servizi: tipo (da parole chiave), accento caldo e foto ──
const KIND_RULES = [
  ['colore', /colo|balay|meche|mèche|schiar|riflessi|cartin|colpi di sole|air ?touch|biond|castan|copper|ramat|cioccol|caramel/],
  ['trattamento', /trattam|curativ|ristruttur|keratin|cheratin|maschera|idrat|laminaz|gloss|lucid/],
  ['modellanti', /modell|perman|ondul|stirat|anticresp|wave|onde|ricc|curl/],
  ['styling', /styl|tagli|piega|acconc|\bcut\b|\bbob\b|bangs|frang/],
];
const kindOf = (text) => {
  const t = String(text || '').toLowerCase();
  const hit = KIND_RULES.find(([, re]) => re.test(t));
  return hit ? hit[0] : null;
};
// band = filo/tinta decorativa; ink = stessa famiglia scurita per il testo (≥ 4.5:1 su bianco)
const CATEGORY_ACCENTS = {
  styling: { band: '#C8617A', ink: '#A94C63', Icon: Scissors },
  colore: { band: '#B8925A', ink: '#8A6A3A', Icon: Palette },
  modellanti: { band: '#B5654A', ink: '#9E5038', Icon: Waves },
  trattamento: { band: '#7D8F72', ink: '#5E6E55', Icon: Droplets },
};
const ACCENT_CYCLE = ['styling', 'colore', 'modellanti', 'trattamento'];
const accentFor = (kind, idx) => {
  if (kind) return CATEGORY_ACCENTS[kind];
  return { ...CATEGORY_ACCENTS[ACCENT_CYCLE[idx % ACCENT_CYCLE.length]], Icon: Sparkles };
};
const isVideoMedia = (item) => item?.file_type === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(item?.img || item?.image_url || '');

// Una foto diversa per categoria: prima quella scelta nel gestionale (config.category_photos), poi i look
// delle tendenze (foto di capelli) il cui titolo richiama la categoria, poi le altre tendenze, e solo in
// ultimo le foto del salone (spesso grafiche o logo).
function assignCategoryPhotos(cats, gallery, trends, chosen = {}) {
  const galleryPool = (gallery || []).filter((g) => g?.image_url && !isVideoMedia(g))
    .map((g) => ({ src: getImageUrl(g.image_url), text: `${g.tag || ''} ${g.label || ''}` }));
  const trendPool = (trends || []).filter((t) => (t?.img || t?.image_url) && !isVideoMedia(t))
    .map((t) => ({ src: optimizeCloudinaryImage(t.img) || getImageUrl(t.image_url), text: `${t.title || ''} ${t.badge || ''}` }));
  const used = new Set();
  const out = {};
  cats.forEach((c) => {
    if (!chosen[c.key]) return;
    out[c.key] = getImageUrl(chosen[c.key]);
    used.add(out[c.key]);
  });
  cats.forEach((c) => {
    if (out[c.key] || !c.kind) return;
    const m = [...trendPool, ...galleryPool].find((p) => !used.has(p.src) && kindOf(p.text) === c.kind);
    if (m) { out[c.key] = m.src; used.add(m.src); }
  });
  const rest = [...trendPool, ...galleryPool.slice(galleryPool.length > cats.length ? 1 : 0)].filter((p) => !used.has(p.src));
  cats.forEach((c) => {
    if (out[c.key]) return;
    const next = rest.find((p) => !used.has(p.src));
    if (next) { out[c.key] = next.src; used.add(next.src); }
  });
  return out;
}

const capitalize = (s) => { const t = String(s || '').trim(); return t ? t[0].toUpperCase() + t.slice(1) : ''; };

function CategoryPhoto({ src, alt, accent }) {
  const [failed, setFailed] = useState(false);
  const Icon = accent.Icon;
  return (
    <span className="block relative aspect-[16/9] overflow-hidden" style={{ background: `${accent.band}1F` }}>
      {src && !failed ? (
        <img src={src} alt={alt} width={800} height={450} loading="lazy" decoding="async" onError={() => setFailed(true)}
          className="site-zoom-img absolute inset-0 w-full h-full object-cover" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-12 h-12" style={{ color: accent.ink }} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

export function ServicesSection({ servicesRef, landingServiceGroups, setShowBooking, bookService, config = {}, gallery = [] }) {
  const [openLandingCats, setOpenLandingCats] = useState({});
  const [trends, setTrends] = useState([]);
  const toggleLCat = (key) => setOpenLandingCats(prev => ({ ...prev, [key]: !prev[key] }));
  const keysSig = (landingServiceGroups?.orderedKeys || []).join('|');
  const staggerProps = useStaggerProps();

  useEffect(() => {
    let alive = true;
    loadPublicTrends().then((list) => { if (alive) setTrends(list); });
    return () => { alive = false; };
  }, []);

  const cats = useMemo(() => (keysSig ? keysSig.split('|') : []).map((key) => {
    const label = getCategoryInfo(key).label;
    return { key, label, kind: kindOf(key) || kindOf(label) };
  }), [keysSig]);
  const chosenPhotos = config.category_photos;
  const photos = useMemo(() => assignCategoryPhotos(cats, gallery, trends, chosenPhotos || {}), [cats, gallery, trends, chosenPhotos]);
  // Descrizione della categoria dal listino del CMS (stessa famiglia o stesso titolo)
  const descFor = (cat) => {
    const list = Array.isArray(config.service_categories) ? config.service_categories : [];
    const hit = list.find((sc) => cat.kind && kindOf(sc?.title) === cat.kind)
      || list.find((sc) => String(sc?.title || '').trim().toLowerCase() === String(cat.label).trim().toLowerCase());
    return capitalize(hit?.desc);
  };

  return (
    <section ref={servicesRef} className="py-20 sm:py-28 relative scroll-mt-16 md:scroll-mt-12" style={{ background: BRAND.cream }}>
      <div className="max-w-6xl mx-auto px-4">
        <AnimatedSection>
          <div className="w-full text-center mb-12">
            <h2 className="text-4xl sm:text-6xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>Cosa Facciamo</h2>
            <p className="text-base text-[#6B4A3C] mt-4 max-w-md mx-auto">Il listino completo: apri una categoria e prenota il servizio che ti interessa.</p>
            <GoldRule />
          </div>
        </AnimatedSection>
        <motion.ul {...staggerProps} className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start max-w-5xl mx-auto">
          {cats.map((cat, idx) => {
            const catServices = landingServiceGroups.groups[cat.key];
            const isOpen = !!openLandingCats[cat.key];
            const panelId = `cat-panel-${cat.key}`;
            const titleId = `cat-title-${cat.key}`;
            const countId = `cat-count-${cat.key}`;
            const descId = `cat-desc-${cat.key}`;
            const accent = accentFor(cat.kind, idx);
            const desc = descFor(cat);
            const Icon = accent.Icon;
            return (
              <motion.li key={cat.key} variants={RISE}>
                <div className="site-zoom rounded-3xl bg-white border border-[#1A0A10]/10 shadow-[0_4px_18px_rgba(26,10,16,0.06)] overflow-hidden">
                  <div className="h-[3px]" style={{ background: accent.band }} aria-hidden="true" />
                  <button type="button" onClick={() => toggleLCat(cat.key)} aria-expanded={isOpen} aria-controls={panelId}
                    aria-labelledby={`${titleId} ${countId}`} aria-describedby={desc ? descId : undefined}
                    className="w-full block text-left text-[#1A0A10] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#A94C63]">
                    <CategoryPhoto key={photos[cat.key] || 'none'} src={photos[cat.key]} alt={cat.label} accent={accent} />
                    <span className="flex items-start justify-between gap-4 px-6 py-5">
                      <span className="min-w-0">
                        <span className="flex items-center gap-2.5">
                          <Icon className="w-5 h-5 shrink-0" style={{ color: accent.ink }} aria-hidden="true" />
                          <span id={titleId} className="text-2xl font-semibold leading-tight" style={{ fontFamily: DISPLAY_FONT }}>{cat.label}</span>
                        </span>
                        {desc && <span id={descId} className="block text-sm text-[#6B4A3C] mt-2 leading-relaxed">{desc}</span>}
                        <span id={countId} className="block text-sm font-semibold mt-3" style={{ color: accent.ink }}>
                          {catServices.length} {catServices.length === 1 ? 'servizio' : 'servizi'}
                        </span>
                      </span>
                      <span className="mt-1 h-10 pl-4 pr-3 shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#1A0A10]/10 text-sm font-bold tracking-wide" style={{ color: accent.ink }} aria-hidden="true">
                        {isOpen ? 'CHIUDI' : 'APRI'}
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ color: accent.ink }} />
                      </span>
                    </span>
                  </button>
                  {isOpen && (
                    <ul id={panelId} className="border-t border-[#D4AF7A]/40 divide-y divide-[#1A0A10]/[0.07]">
                      {catServices.map((service) => (
                        <li key={service.id} className="flex items-center justify-between gap-4 px-6 py-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-base text-[#2D1B14]">{service.name}</p>
                            {service.duration > 0 && (
                              <p className="flex items-center gap-1 text-sm text-[#6B4A3C] mt-0.5"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{service.duration} min</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {service.price > 0 && <span className="font-bold text-lg tabular-nums" style={{ color: accent.ink }}>€{service.price}</span>}
                            <button onClick={() => bookService ? bookService(service.id) : setShowBooking(true)}
                              aria-label={`Prenota ${service.name}`}
                              className="min-h-[44px] px-4 rounded-full bg-[#A94C63] hover:bg-[#8F3F54] text-white text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF7A] focus-visible:ring-offset-2">
                              Prenota
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}

export function SalonSection({ salonPhotos }) {
  const staggerProps = useStaggerProps();
  if (!salonPhotos?.length) return null;
  return (
    <section className="py-20 sm:py-28 relative" style={{ background: BRAND.espresso }}>
      <div className="max-w-7xl mx-auto px-4 text-center">
        <AnimatedSection>
          <div className="mb-12">
            <h2 className="text-4xl sm:text-6xl font-semibold text-white" style={{ fontFamily: DISPLAY_FONT }}>Il Nostro Salone</h2>
            <GoldRule />
          </div>
        </AnimatedSection>
        <motion.div {...staggerProps} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {salonPhotos.map((item, i) => (
            <motion.div key={item.id || i} variants={RISE} className="site-zoom relative rounded-3xl overflow-hidden aspect-square border border-[#D4AF7A]/20 shadow-2xl">
              <img src={getImageUrl(item?.image_url, 600)} alt="" width={600} height={600} loading="lazy" decoding="async" className="site-zoom-img w-full h-full object-cover" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function AboutSection({ config, salonPhotos }) {
  return (
    <section className="py-20 sm:py-32 relative overflow-hidden" style={{ background: BRAND.espressoSoft }}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-0 opacity-[0.03]">
        <span aria-hidden="true" style={{ fontFamily: DISPLAY_FONT, fontSize: '35vw', fontWeight: 900, lineHeight: 1, color: '#fff' }}>1983</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            {salonPhotos.length > 0 && (
              <AnimatedSection>
                <div className="relative">
                  <div className="absolute -inset-4 border border-white/10 rounded-[3rem] z-0" />
                  <div className="rounded-[3.5rem] overflow-hidden aspect-[4/5] lg:aspect-auto lg:h-[650px] shadow-2xl relative z-10 border border-white/5">
                    <img src={getImageUrl(salonPhotos[0]?.image_url, 900)} alt="Bruno Melito" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                  </div>

                </div>
              </AnimatedSection>
            )}
          </div>

          <div className="lg:col-span-7">
            <AnimatedSection delay={0.2}>
              <div className="max-w-2xl">
                <h2 className="text-4xl sm:text-7xl font-semibold mb-10 text-white leading-[0.95] italic" style={{ fontFamily: DISPLAY_FONT }}>
                  {config.about_title || 'Dal 1983 con Passione'}<br />
                  <span style={{ color: BRAND.gold }}>E Stile</span>
                </h2>
                <div className="space-y-8">
                  <p className="text-xl sm:text-2xl text-white/80 font-medium leading-relaxed italic border-l border-[#D4AF7A] pl-8 py-2" style={{ fontFamily: DISPLAY_FONT }}>
                    {config.about_text || 'Oltre 40 anni di eccellenza nell’hair styling d’alta moda.'}
                  </p>
                  <p className="text-white/75 text-base leading-relaxed pl-9">
                    {config.about_text_2 || 'Abbiamo introdotto una nuova linea di prodotti altamente curativi, di ultima generazione: senza parabeni, solfati e sale. Le nostre colorazioni sono senza ammoniaca, arricchite con cheratina e seta.'}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 mt-12 pl-9">
                  {[
                    ['Consulenza su misura', 'Consulenza gratuita sui tuoi capelli: ti consigliamo il taglio o il trattamento più adatto, senza vendite forzate.'],
                    ['Accoglienza', 'Un ambiente rilassante e familiare, con caffè o acqua di benvenuto e puntualità negli appuntamenti.'],
                    ['Sposa e cerimonie', 'Acconciature per le occasioni speciali, anche con prova.'],
                  ].map(([titolo, testo]) => (
                    <div key={titolo} className="group flex items-start gap-4 p-6 rounded-[2rem] bg-white/5 border border-white/10 hover:bg-[#C8617A] transition-all duration-500 cursor-default">
                      <div className="w-11 h-11 shrink-0 rounded-2xl bg-[#D4AF7A]/20 flex items-center justify-center group-hover:bg-white transition-colors">
                        <CheckCircle className="text-[#D4AF7A] group-hover:text-[#C8617A]" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold uppercase text-sm tracking-wider mb-1">{titolo}</h3>
                        <p className="text-white/75 text-sm leading-snug">{testo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
}

// Colonne bilanciate: niente card "orfana" sull'ultima riga (es. 4 offerte → 2 + 2)
const balancedGridCols = (n) => {
  if (n <= 1) return 'grid-cols-1 max-w-md mx-auto';
  if (n === 2 || n === 4) return 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto';
  return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
};

// Gruppo "Offerte": promozioni + Servizi Express + (opzionale) Gift card, in un'unica sezione
export function PromotionsSection({ publicPromos = [], setShowBooking, bookPromo, smartServices = [], bookService, giftCard = null }) {
  const reduce = useReducedMotion();
  const staggerProps = useStaggerProps();
  if (!publicPromos.length && !smartServices.length) return null;
  const open = (promo) => (bookPromo ? bookPromo(promo) : setShowBooking(true));
  return (
    <section className="py-20 sm:py-28 relative" style={{ background: BRAND.espresso }} aria-labelledby="offerte-title">
      <div className="max-w-6xl mx-auto px-4 relative">
        <AnimatedSection>
          <div className="text-center mb-12">
            <h2 id="offerte-title" className="text-4xl sm:text-6xl font-semibold text-white" style={{ fontFamily: DISPLAY_FONT }}>Offerte da non perdere</h2>
            <GoldRule />
          </div>
        </AnimatedSection>

        {publicPromos.length > 0 && (
          <motion.div {...staggerProps} className={`grid gap-6 ${balancedGridCols(publicPromos.length)}`}>
            {publicPromos.map((promo, idx) => (
              <motion.div key={promo.id || idx} variants={RISE} className="flex">
              <article
                className="site-lift w-full rounded-3xl p-8 flex flex-col text-left bg-[#FDF8F5] border border-[#D4AF7A] shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
                style={{ '--lift-shadow': '0 30px 60px rgba(0,0,0,0.42)' }}
              >
                <span className="relative overflow-hidden inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full bg-[#D4AF7A] text-[#1A0A10] text-xs font-bold uppercase tracking-wider">
                  <Gift className="w-3.5 h-3.5" aria-hidden="true" /> Offerta
                  {/* Un solo riflesso quando la card entra in vista */}
                  {!reduce && (
                    <motion.span aria-hidden="true" className="absolute inset-y-0 -left-1/2 w-1/2 pointer-events-none"
                      style={{ background: 'linear-gradient(100deg, transparent, rgba(255,255,255,0.8), transparent)' }}
                      variants={{ hidden: { x: '0%', opacity: 0 }, visible: { x: '400%', opacity: [0, 1, 0], transition: { duration: 0.9, ease: _SITE_EASE, delay: 0.35 } } }} />
                  )}
                </span>
                <h3 className="text-2xl font-semibold text-[#1A0A10] mt-5 leading-tight" style={{ fontFamily: DISPLAY_FONT }}>{promo.name}</h3>
                {promo.description && <p className="text-[#6B4A3C] text-base mt-3 leading-relaxed">{promo.description}</p>}
                {promo.free_service_name && (
                  <p className="mt-5 pt-4 border-t border-[#D4AF7A]/50 text-[#A94C63] font-bold text-lg">In omaggio: {promo.free_service_name}</p>
                )}
                {promo.promo_code && (
                  <p className="mt-3 text-sm text-[#2D1B14]">Codice <span className="font-bold tracking-wider">{promo.promo_code}</span></p>
                )}
                <button onClick={() => open(promo)} className={`${CTA_PRIMARY} w-full mt-8`}>
                  Prenota l'offerta
                </button>
              </article>
              </motion.div>
            ))}
          </motion.div>
        )}

        {smartServices.length > 0 && (
          <div className={publicPromos.length > 0 ? 'mt-16 pt-12 border-t border-[#D4AF7A]/25' : ''}>
            <div className="text-center mb-8">
              <h3 className="text-3xl md:text-4xl font-semibold text-white" style={{ fontFamily: DISPLAY_FONT }}>Servizi Express</h3>
              <p className="text-base text-white/75 mt-2">Ideali per un cambio look rapido. Prenota online in 1 minuto.</p>
            </div>
            <motion.div {...staggerProps} className={`grid gap-4 ${balancedGridCols(smartServices.length)}`}>
              {smartServices.map((s) => (
                <motion.div key={s.id} variants={RISE} className="rounded-2xl p-6 text-center bg-[#24121A] border border-[#D4AF7A]/35">
                  <h4 className="font-semibold text-xl text-white" style={{ fontFamily: DISPLAY_FONT }}>{s.name}</h4>
                  <p className="mt-2 text-white/75 text-sm">
                    {s.duration > 0 && <span>{s.duration} minuti</span>}
                    {s.duration > 0 && s.price > 0 && <span aria-hidden="true"> · </span>}
                    {s.price > 0 && <span className="text-[#D4AF7A] font-bold text-lg tabular-nums">€{s.price}</span>}
                  </p>
                  <button onClick={() => (bookService ? bookService(s.id) : setShowBooking(true))} className={`${CTA_PRIMARY_CORE} min-h-[48px] px-6 text-sm mt-5`} aria-label={`Prenota ${s.name}`}>
                    Prenota
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}

        {giftCard && <div className="mt-16">{giftCard}</div>}
      </div>
    </section>
  );
}

// Foto "sparsa" tra le sezioni: entra in scena con rotazione, riflesso specchiato
// sotto e glow colorato all'hover. Alterna lato (sinistra/destra) in base all'indice.
export function PhotoInterlude({ photo, index = 0, T }) {
  const reduce = useReducedMotion();
  if (!photo) return null;
  const flip = index % 2 === 1;
  const isVideo = photo.file_type === 'video';
  const src = optimizeCloudinaryImage(getMediaUrl(photo.image_url), 1200);
  return (
    <section className="py-8 sm:py-14 relative overflow-hidden" style={{ background: '#020205' }}>
      {/* alone colorato */}
      <div className={`absolute top-1/2 -translate-y-1/2 w-80 h-80 rounded-full blur-[120px] opacity-25 pointer-events-none ${flip ? 'left-0' : 'right-0'}`}
        style={{ background: flip ? T.accent : T.primary }} />
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.92, rotate: flip ? 2.5 : -2.5 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1, ease: _SITE_EASE }}
          className={`relative w-full sm:max-w-[72%] ${flip ? 'sm:ml-auto' : 'sm:mr-auto'}`}
        >
          <div className="group relative overflow-hidden rounded-[2.5rem] shadow-2xl border border-white/10">
            {isVideo ? (
              <video src={src} className="w-full h-[240px] sm:h-[420px] object-cover" autoPlay muted loop playsInline preload="metadata" />
            ) : (
              <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-[240px] sm:h-[420px] object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
            )}
            {/* velo specchiato */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/12 via-transparent to-white/5 pointer-events-none" />
            {/* glow colorato all'hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[2.5rem]"
              style={{ boxShadow: `inset 0 0 70px ${(flip ? T.accent : T.primary)}55, 0 0 100px ${(flip ? T.accent : T.primary)}30` }} />
          </div>
          {/* RIFLESSO SPECCHIATO sotto (solo immagini, desktop) */}
          {!isVideo && (
            <div className="hidden sm:block h-24 overflow-hidden rounded-b-[2.5rem]"
              style={{ transform: 'scaleY(-1)', opacity: 0.32, WebkitMaskImage: 'linear-gradient(black, transparent)', maskImage: 'linear-gradient(black, transparent)' }}>
              <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-bottom blur-[1px]" />
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

// Quante recensioni affiancate: 1 su telefono, 2 su tablet, 3 su desktop
const PER_VIEW_QUERIES = [['(min-width: 1024px)', 3], ['(min-width: 768px)', 2]];
const readPerView = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 1;
  const hit = PER_VIEW_QUERIES.find(([q]) => window.matchMedia(q).matches);
  return hit ? hit[1] : 1;
};
function usePerView() {
  const [perView, setPerView] = useState(readPerView);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mqs = PER_VIEW_QUERIES.map(([q]) => window.matchMedia(q));
    const onChange = () => setPerView(readPerView());
    mqs.forEach((m) => m.addEventListener?.('change', onChange));
    return () => mqs.forEach((m) => m.removeEventListener?.('change', onChange));
  }, []);
  return perView;
}

const AUTOPLAY_MS = 6000;
const starsOf = (r) => Math.max(0, Math.min(5, Math.round(Number(r.rating) || 0)));

export function ReviewsSection({ reviews, config }) {
  const reduce = useReducedMotion();
  const perView = usePerView();
  const regionRef = useRef(null);
  const touchX = useRef(null);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [stopped, setStopped] = useState(false); // l'utente ha preso il controllo: niente più scorrimento automatico
  const inView = useInView(regionRef, { margin: '-80px' });

  const total = reviews?.length || 0;
  const maxIndex = Math.max(0, total - perView);
  const current = Math.min(index, maxIndex);
  const autoplay = !reduce && !stopped && maxIndex > 0;
  const running = autoplay && !hovered && !focused && !tabHidden && inView;

  useEffect(() => {
    const onVis = () => setTabHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const id = setTimeout(() => setIndex(current >= maxIndex ? 0 : current + 1), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [running, current, maxIndex]);

  if (!total) return null;

  const goTo = (i) => { setStopped(true); setIndex(Math.max(0, Math.min(maxIndex, i))); };
  const prev = () => goTo(current <= 0 ? maxIndex : current - 1);
  const next = () => goTo(current >= maxIndex ? 0 : current + 1);
  const onTouchStart = (e) => { setStopped(true); touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    const start = touchX.current;
    touchX.current = null;
    const end = e.changedTouches[0]?.clientX;
    if (start == null || end == null || Math.abs(end - start) < 50) return;
    if (end < start) next(); else prev();
  };
  const ctrlBtn = 'w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-full border border-[#1A0A10]/15 bg-white text-[#1A0A10] hover:border-[#A94C63] hover:text-[#A94C63] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A94C63]';

  return (
    <section className="py-20 sm:py-28 overflow-hidden" style={{ background: BRAND.blush }}>
      <AnimatedSection>
        <div className="max-w-6xl mx-auto px-4 text-center mb-12">
          <h2 className="text-4xl sm:text-6xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>Cosa Dicono di Noi</h2>
          <GoldRule />
        </div>
      </AnimatedSection>
      <AnimatedSection className="max-w-6xl mx-auto px-4">
        <div
          ref={regionRef}
          role="region"
          aria-roledescription="carosello"
          aria-label="Recensioni dei clienti"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="overflow-hidden -mx-3">
            <div
              className="flex"
              aria-live={running ? 'off' : 'polite'}
              style={{
                transform: `translateX(-${(current * 100) / perView}%)`,
                transition: reduce ? 'none' : 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {reviews.map((r, i) => {
                const visible = i >= current && i < current + perView;
                const stars = starsOf(r);
                return (
                  <div key={r.id || i} role="group" aria-roledescription="recensione" aria-label={`${i + 1} di ${total}`}
                    aria-hidden={!visible} inert={!visible}
                    className="shrink-0 px-3 flex" style={{ width: `${100 / perView}%` }}>
                    <figure className="w-full rounded-3xl p-8 bg-white border border-[#1A0A10]/10 shadow-[0_8px_24px_rgba(26,10,16,0.06)] flex flex-col justify-between">
                      <div>
                        <div className="flex gap-1.5 mb-5 text-[#A9814A]" role="img" aria-label={`${stars} stelle su 5`}>
                          {Array.from({ length: stars }, (_, s) => <Star key={s} size={18} fill="currentColor" aria-hidden="true" />)}
                        </div>
                        <blockquote className="text-[#2D1B14] text-base italic mb-8 leading-relaxed">"{r.text}"</blockquote>
                      </div>
                      <figcaption className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-[#1A0A10] flex items-center justify-center text-[#D4AF7A] text-lg" style={{ fontFamily: DISPLAY_FONT }} aria-hidden="true">{(r.name || 'C')[0]}</div>
                        <div>
                          <p className="text-[#1A0A10] font-bold text-sm">{r.name}</p>
                          <p className="text-xs text-[#6B4A3C]">Recensione Google</p>
                        </div>
                      </figcaption>
                    </figure>
                  </div>
                );
              })}
            </div>
          </div>

          {maxIndex > 0 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button type="button" onClick={prev} className={ctrlBtn} aria-label="Recensione precedente">
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              </button>
              <div className="flex items-center">
                {Array.from({ length: maxIndex + 1 }, (_, k) => (
                  <button key={k} type="button" onClick={() => goTo(k)}
                    aria-label={perView === 1 ? `Recensione ${k + 1} di ${total}` : `Recensioni da ${k + 1} a ${k + perView}`}
                    aria-current={k === current ? 'true' : undefined}
                    className="w-11 h-11 inline-flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A94C63]">
                    <span aria-hidden="true"
                      className={`block w-2 h-2 rounded-full transition-[transform,background-color] duration-300 ${k === current ? 'bg-[#A94C63] scale-150' : 'bg-[#1A0A10]/25'}`} />
                  </button>
                ))}
              </div>
              <button type="button" onClick={next} className={ctrlBtn} aria-label="Recensione successiva">
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </AnimatedSection>
      {config?.maps_url && (
        <div className="text-center px-4 mt-12">
          <a href={config.maps_url} target="_blank" rel="noopener noreferrer" className={CTA_OUTLINE_LIGHT}>
            Leggi le altre recensioni su Google
          </a>
        </div>
      )}
    </section>
  );
}

// Banda "Dal {anno}": solo numeri ricavati dai dati del salone, contati una volta quando entrano in vista
function CountUp({ value, decimals = 0, suffix = '' }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (reduce || !inView) return undefined;
    let raf;
    const t0 = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      setShown(value * (1 - Math.pow(1 - p, 4)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, value]);
  const fmt = (n) => n.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (
    <span ref={ref}>
      <span aria-hidden="true">{fmt(reduce ? value : shown)}{suffix}</span>
      <span className="sr-only">{fmt(value)}{suffix}</span>
    </span>
  );
}

export function HeritageBand({ config = {}, servicesCount = 0, reviews = [] }) {
  const staggerProps = useStaggerProps();
  const thisYear = new Date().getFullYear();
  const founded = parseInt(String(config.year_founded || '').trim(), 10);
  if (!founded || founded < 1900 || founded > thisYear) return null;

  const stats = [];
  const years = thisYear - founded;
  if (years > 0) stats.push({ key: 'anni', value: years, label: 'anni di esperienza' });
  if (servicesCount > 0) stats.push({ key: 'servizi', value: servicesCount, label: 'servizi in listino' });
  const ratings = (reviews || []).map((r) => Number(r.rating)).filter((n) => n > 0 && n <= 5);
  if (ratings.length) {
    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    stats.push({ key: 'voto', value: Math.round(avg * 10) / 10, decimals: 1, star: true, label: `media su ${ratings.length} ${ratings.length === 1 ? 'recensione' : 'recensioni'}` });
  }

  return (
    <section className="py-16 sm:py-20" style={{ background: BRAND.cream }} aria-labelledby="heritage-title">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <AnimatedSection>
          <h2 id="heritage-title" className="text-4xl sm:text-5xl font-semibold text-[#1A0A10]" style={{ fontFamily: DISPLAY_FONT }}>
            Dal <span className="italic text-[#A94C63]">{founded}</span>
          </h2>
          <GoldRule />
        </AnimatedSection>
        {stats.length > 0 && (
          <motion.dl {...staggerProps} className={`mt-10 grid grid-cols-1 gap-8 ${stats.length === 2 ? 'sm:grid-cols-2' : stats.length >= 3 ? 'sm:grid-cols-3' : ''} sm:gap-0 sm:divide-x sm:divide-[#D4AF7A]/50`}>
            {stats.map((s) => (
              <motion.div key={s.key} variants={RISE} className="flex flex-col-reverse items-center px-6">
                <dt className="mt-2 text-sm font-semibold uppercase tracking-wider text-[#6B4A3C]">{s.label}</dt>
                <dd className="flex items-center gap-2 text-5xl sm:text-6xl font-semibold text-[#1A0A10] tabular-nums" style={{ fontFamily: DISPLAY_FONT }}>
                  <CountUp value={s.value} decimals={s.decimals || 0} />
                  {s.star && <Star className="w-7 h-7 text-[#A9814A]" fill="currentColor" aria-hidden="true" />}
                </dd>
              </motion.div>
            ))}
          </motion.dl>
        )}
      </div>
    </section>
  );
}

export function GallerySection({ hairstylePhotos }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const imagePhotos = hairstylePhotos.filter(p => p.file_type !== 'video');

  return (
    <>
      <section id="gallery-edit" className="py-24 sm:py-32 overflow-hidden relative" style={{ background: '#020205' }}>
        <style>{`
          @keyframes galleryShine {
            0% { transform: translateX(-100%) skewX(-20deg); }
            100% { transform: translateX(200%) skewX(-20deg); }
          }
          .gallery-photo-shine {
            position: absolute;
            top: 0;
            left: -100%;
            width: 60%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
            transform: skewX(-20deg);
            pointer-events: none;
            opacity: 0;
            border-radius: 2.5rem;
          }
          @media (prefers-reduced-motion: no-preference) {
            .group:hover .gallery-photo-shine {
              animation: galleryShine 1.2s ease-in-out;
              opacity: 1;
            }
          }
        `}</style>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C8617A]/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: _SITE_EASE }}
            className="mb-20"
          >
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
              <h2
                className="font-black text-white uppercase italic"
                style={{ 
                  fontFamily: "'Playfair Display', serif", 
                  fontSize: 'clamp(3rem, 10vw, 8rem)', 
                  lineHeight: 0.8
                }}
              >
                The<br />
                <span className="text-transparent" style={{ WebkitTextStroke: '2px white' }}>Edit</span>
              </h2>
              <div className="max-w-xs">
                <p className="text-white/40 text-sm leading-relaxed mb-6 font-medium italic">
                  "La bellezza è l’unica forma di ribellione che ci è rimasta."
                </p>
                <div className="h-px w-full bg-gradient-to-r from-[#D4AF7A]/50 to-transparent" />
              </div>
            </div>
          </motion.div>

          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {hairstylePhotos.map((item, idx) => (
              <motion.div
                key={item.id || idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.8, ease: _SITE_EASE, delay: idx * 0.05 }}
                onClick={() => item.file_type !== 'video' && setLightboxIdx(imagePhotos.indexOf(item))}
                className="group relative overflow-hidden rounded-[2.5rem] bg-zinc-900 border border-white/10 cursor-zoom-in shadow-2xl transition-all duration-500 hover:border-[#D4AF7A]/50 break-inside-avoid"
              >
                {item.file_type === 'video' ? (
                  <video
                    src={getMediaUrl(item?.image_url)}
                    className="w-full h-auto object-cover block transition-transform duration-1000 group-hover:scale-110"
                    autoPlay muted loop playsInline preload="metadata"
                  />
                ) : (
                  <img
                    src={getImageUrl(item?.image_url, 800)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                )}
                {/* Mirror/Reflection gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/8 via-transparent to-white/3 pointer-events-none rounded-[2.5rem]" />
                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                {/* Shine effect on hover */}
                <div className="gallery-photo-shine" />
                {/* Glow effect on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-[2.5rem]"
                  style={{
                    boxShadow: 'inset 0 0 50px rgba(168, 85, 247, 0.25), 0 0 80px rgba(168, 85, 247, 0.15)',
                  }}
                />
              </motion.div>
            ))}
          </div>


        </div>
      </section>

      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-[100] bg-black/98 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)}>
          <button className="absolute top-8 right-8 text-white"><X size={40} /></button>
          <img src={getImageUrl(imagePhotos[lightboxIdx]?.image_url, 1600)} className="max-w-full max-h-[90vh] rounded-3xl object-contain shadow-2xl" />
        </div>
      )}
    </>
  );
}

const TEAM_NOTES = {
  bruno: 'Il titolare. Serio e professionale al lavoro, simpaticissimo fuori.',
  bruna: 'Stilista eccellente, cordiale e simpatica: accoglierà ogni tua richiesta.',
  roberta: 'Stilista eccellente, cordiale e simpatica: ogni tua richiesta è la benvenuta.',
};

// Ignora il numero d'ordine iniziale ("1 BRUNO") e usa la nota del primo nome riconosciuto (es. "2 BRUNA e ROBERTA" → Bruna)
const noteFor = (name) => {
  const words = String(name || '').toLowerCase().replace(/^\s*\d+/, '').split(/[^\p{L}]+/u).filter(Boolean);
  const first = words.find((w) => Object.prototype.hasOwnProperty.call(TEAM_NOTES, w));
  return first ? TEAM_NOTES[first] : null;
};

export function TeamSection({ operators, setShowBooking }) {
  const staggerProps = useStaggerProps();
  if (!operators?.length) return null;
  return (
    <section className="py-20 sm:py-28 relative" style={{ background: BRAND.espresso }}>
      <div className="max-w-5xl mx-auto px-4 text-center">
        <AnimatedSection>
          <h2 className="text-4xl sm:text-6xl font-semibold text-white" style={{ fontFamily: DISPLAY_FONT }}>
            Chi si prende <span className="italic" style={{ color: BRAND.gold }}>cura di te</span>
          </h2>
          <GoldRule className="mx-auto mt-6 mb-6" />
          <p className="text-white/80 text-lg max-w-xl mx-auto mb-14">Un gruppo affiatato che ti accoglie con un sorriso e ti ascolta prima di prendere in mano le forbici.</p>
        </AnimatedSection>
        <motion.div {...staggerProps} className="flex flex-wrap justify-center gap-x-12 gap-y-10">
          {operators.filter(o => o.active !== false).map((op) => (
            <motion.div key={op.id} variants={RISE} className="w-56">
              <div className="w-28 h-28 mx-auto rounded-full mb-4 p-1 border border-[#D4AF7A]/60">
                <div className="w-full h-full rounded-full bg-[#24121A] flex items-center justify-center text-4xl text-[#D4AF7A] uppercase" style={{ fontFamily: DISPLAY_FONT }} aria-hidden="true">{op.name.charAt(0)}</div>
              </div>
              <h3 className="text-white font-semibold text-lg" style={{ fontFamily: DISPLAY_FONT }}>{op.name}</h3>
              {noteFor(op.name) && (
                <p className="text-white/75 text-sm mt-2 leading-snug">{noteFor(op.name)}</p>
              )}
            </motion.div>
          ))}
        </motion.div>
        <button onClick={() => setShowBooking(true)} className={`${CTA_PRIMARY} mt-14`}>Prenota e vieni a conoscerci</button>
      </div>
    </section>
  );
}

// Striscia in linea sotto l'hero: resta finché l'utente non la chiude con la X
export function WelcomeBanner({ setShowBooking }) {
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem('bmh_welcome_dismissed'); } catch { return true; }
  });
  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem('bmh_welcome_dismissed', '1'); } catch { /* storage non disponibile */ }
  };

  if (!visible) return null;

  return (
    <aside className="bg-[#F6ECE6] border-b border-[#D4AF7A]/50" aria-label="Benvenuta">
      <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3 sm:gap-4">
        <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#1A0A10] items-center justify-center shrink-0" aria-hidden="true">
          <Gift className="w-5 h-5 text-[#D4AF7A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#1A0A10] font-semibold text-lg leading-tight" style={{ fontFamily: DISPLAY_FONT }}>Prima volta da noi?</p>
          <p className="text-[#6B4A3C] text-sm">Un <strong className="text-[#1A0A10]">vantaggio speciale</strong> ti aspetta al tuo primo appuntamento!</p>
        </div>
        <button onClick={() => setShowBooking(true)} className={`${CTA_PRIMARY_CORE} min-h-[44px] px-5 text-sm shrink-0`}>Prenota</button>
        <button onClick={dismiss} aria-label="Chiudi avviso"
          className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-full text-[#6B4A3C] hover:text-[#1A0A10] hover:bg-[#1A0A10]/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A94C63]">
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}

// embedded = dentro il gruppo "Offerte" (nessuna sezione propria)
// Grafiche di Natale (fatte con Canva): dopo questa data torna da sola la versione normale
const CHRISTMAS_UNTIL = '2027-01-06';

function GiftWhatsAppButton({ whatsapp }) {
  const text = encodeURIComponent('Ciao! Vorrei una Gift Card del salone.');
  return (
    <a href={`https://wa.me/${whatsapp}?text=${text}`} target="_blank" rel="noopener noreferrer" className={CTA_PRIMARY}>
      <Gift className="w-5 h-5" aria-hidden="true" /> Chiedi la Gift Card su WhatsApp
    </a>
  );
}

function ChristmasGiftCard({ whatsapp }) {
  return (
    <div className="rounded-3xl border border-[#D4AF7A]/60 bg-[#24121A] overflow-hidden text-white">
      <img src="/natale/banner-natale.jpg" alt="Natale da Bruno Melito. Regala Bellezza a chi ami." width="1600" height="800" loading="lazy" className="w-full h-auto block" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center px-6 py-10 sm:p-12">
        <img src="/natale/gift-card-natale.jpg" alt="Gift Card Bruno Melito Hair con la scritta Regala Bellezza" width="1000" height="773" loading="lazy" className="w-full h-auto rounded-xl border border-[#D4AF7A]/40 shadow-2xl" />
        <div className="text-center md:text-left">
          <h2 className="text-4xl sm:text-5xl font-semibold mb-5 leading-tight italic" style={{ fontFamily: DISPLAY_FONT }}>Regala Bellezza</h2>
          <p className="text-lg text-white/80 mb-8 leading-relaxed">Sorprendi chi ami con un'esperienza di lusso firmata Bruno Melito.</p>
          <GiftWhatsAppButton whatsapp={whatsapp} />
        </div>
      </div>
    </div>
  );
}

export function GiftCardSection({ whatsapp, embedded = false }) {
  const isChristmas = new Date().toISOString().slice(0, 10) <= CHRISTMAS_UNTIL;
  const card = isChristmas ? <ChristmasGiftCard whatsapp={whatsapp} /> : (
    <div className="rounded-3xl border border-[#D4AF7A]/60 bg-[#24121A] px-6 py-12 sm:p-16 text-center text-white">
      <Gift className="w-8 h-8 text-[#D4AF7A] mx-auto mb-5" aria-hidden="true" />
      <h2 className="text-4xl sm:text-6xl font-semibold mb-5 leading-tight italic" style={{ fontFamily: DISPLAY_FONT }}>Regala Bellezza</h2>
      <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">Sorprendi chi ami con un'esperienza di lusso firmata Bruno Melito.</p>
      <GiftWhatsAppButton whatsapp={whatsapp} />
    </div>
  );
  if (embedded) return card;
  return (
    <section className="py-20 sm:py-28" style={{ background: BRAND.espresso }}>
      <div className="max-w-5xl mx-auto px-4">{card}</div>
    </section>
  );
}

export function ContactSection({ contactRef, config, phones, setShowBooking }) {
  return (
    <section ref={contactRef} className="py-20 sm:py-28 relative scroll-mt-16 md:scroll-mt-12" style={{ background: BRAND.espressoSoft }}>
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 text-white">
         <div>
            <h2 className="text-5xl sm:text-7xl font-semibold mb-12 italic" style={{ fontFamily: DISPLAY_FONT }}>Vieni a<br/>Trovarci</h2>
            <div className="space-y-10">
               <div className="flex gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#1A0A10] flex items-center justify-center shrink-0 border border-[#D4AF7A]/40" aria-hidden="true">
                     <MapPin size={26} className="text-[#D4AF7A]" />
                  </div>
                  <div>
                     <p className="font-semibold text-xl" style={{ fontFamily: DISPLAY_FONT }}>Indirizzo</p>
                     <p className="text-white/80 mt-2 text-lg">{config.address || 'Via Vito Nicola Melorio 101, Santa Maria Capua Vetere (CE)'}</p>
                  </div>
               </div>
               {phones.length > 0 && (
                 <div className="flex gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-[#1A0A10] flex items-center justify-center shrink-0 border border-[#D4AF7A]/40" aria-hidden="true">
                       <Phone size={26} className="text-[#D4AF7A]" />
                    </div>
                    <div>
                       <p className="font-semibold text-xl" style={{ fontFamily: DISPLAY_FONT }}>Telefono</p>
                       <div className="mt-1">
                          {phones.map((p, i) => (
                            <a key={i} href={`tel:${String(p).replace(/\s/g, '')}`} className="flex items-center min-h-[44px] text-white/80 hover:text-white text-lg underline-offset-4 hover:underline">{p}</a>
                          ))}
                       </div>
                    </div>
                 </div>
               )}
            </div>
         </div>
         <div className="bg-[#1A0A10] rounded-3xl px-6 py-12 sm:p-16 border border-[#D4AF7A]/40 flex flex-col justify-center text-center">
            <h3 className="text-4xl sm:text-5xl font-semibold mb-6 italic leading-tight" style={{ fontFamily: DISPLAY_FONT }}>Pronto per il<br/>Cambiamento?</h3>
            <p className="text-white/80 mb-10 text-lg">La tua bellezza merita il meglio assoluto. Prenota ora il tuo trattamento esclusivo.</p>
            <button onClick={() => setShowBooking(true)} className={`${CTA_PRIMARY_CORE} min-h-[56px] px-8 text-base`}>
               Prenota Online
            </button>
            <div className="mt-10 flex justify-center gap-2">
               {SOCIAL_LINKS.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label} title={link.label}
                    className="w-11 h-11 inline-flex items-center justify-center rounded-full text-white/75 hover:text-white hover:bg-white/10 transition-colors">
                     <link.icon size={22} aria-hidden="true" />
                  </a>
               ))}
            </div>
         </div>
      </div>
    </section>
  );
}

export function QRCodeSection({ T }) {
  const siteUrl = 'https://brunomelitohair.it';
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(siteUrl)}&color=ffffff&bgcolor=000000&margin=10`;

  return (
    <section className="py-24 sm:py-32 relative" style={{ background: '#0a0a0f' }}>
      <div className="max-w-xl mx-auto px-4 text-center">
        <AnimatedSection>
                    <h2 className="text-4xl sm:text-5xl font-semibold text-white mb-4 italic uppercase" style={{ fontFamily: T.fontDisplay }}>
            Scansiona e Prenota
          </h2>
          <div className="inline-flex flex-col items-center gap-6 mt-10">
            <div className="p-6 rounded-[2.5rem] shadow-2xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
              <img src={qrUrl} alt="QR Code" width={200} height={200} className="rounded-2xl" />
            </div>
            <p className="text-white/75 text-xs font-bold tracking-[0.2em] uppercase">brunomelitohair.it</p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export function GalleryStrip() {
  return null;
}
